/**
 * Fonts — load custom @font-face declarations and await them.
 *
 * Phaser renders text via the Canvas 2D `fillText()` API which uses the
 * browser's font loading state. If the game tries to render text before
 * the custom font has finished loading, the canvas falls back to the
 * default system font and the resulting glyphs are baked into Phaser's
 * text texture cache — meaning the font WILL NOT update later, even
 * after it loads. The fix is to wait for `document.fonts.load()` to
 * resolve BEFORE Phaser starts rendering.
 *
 * Supported file formats (any modern browser):
 *   - WOFF2  (recommended; smallest, ~30% smaller than TTF, IE no, all others yes)
 *   - WOFF   (broad support including IE 9+)
 *   - TTF    (TrueType — universal, larger than WOFF2)
 *   - OTF    (OpenType — universal, larger than WOFF2)
 *
 * Usage from `bootGame()`:
 *
 *   await Fonts.load([
 *     { family: 'PixelFont', url: 'assets/fonts/pixel.woff2', format: 'woff2' },
 *     { family: 'TitleFont', url: 'assets/fonts/title.ttf' } // format auto-detected
 *   ]);
 *
 * Once resolved, the fonts are registered with `document.fonts` and any
 * Phaser text using `fontFamily: 'PixelFont'` will render correctly.
 */
const FORMAT_BY_EXT = {
  '.woff2': 'woff2',
  '.woff':  'woff',
  '.ttf':   'truetype',
  '.otf':   'opentype'
};

export class Fonts {
  /**
   * Load + register a list of custom fonts. Waits for all of them to
   * be ready before resolving. Safe no-op outside browsers (Node tests).
   *
   * @param {Array<{family: string, url: string, format?: string, descriptors?: object}>} list
   * @returns {Promise<void>}
   */
  static async load(list) {
    if (!Array.isArray(list) || list.length === 0) return;
    if (typeof document === 'undefined' || !document.fonts || typeof FontFace === 'undefined') return;

    const promises = [];
    for (const entry of list) {
      if (!entry || !entry.family || !entry.url) continue;
      const format = entry.format || guessFormat(entry.url) || 'truetype';
      const src = "url('" + entry.url + "') format('" + format + "')";
      const face = new FontFace(entry.family, src, entry.descriptors || {});
      promises.push(
        face.load().then((loaded) => {
          document.fonts.add(loaded);
        }).catch((err) => {
          if (typeof console !== 'undefined') {
            console.warn('[Fonts] failed to load ' + entry.family + ': ' + (err && err.message || err));
          }
        })
      );
    }

    await Promise.all(promises);

    // Belt-and-braces: ask the browser to confirm each family is ready
    // for use at a sane size. Resolves quickly because the FontFace
    // objects above are already in document.fonts.
    const checks = list
      .filter((e) => e && e.family)
      .map((e) => document.fonts.load('16px "' + e.family + '"').catch(() => {}));
    await Promise.all(checks);
  }
}

function guessFormat(url) {
  const lower = url.toLowerCase();
  for (const ext of Object.keys(FORMAT_BY_EXT)) {
    if (lower.endsWith(ext)) return FORMAT_BY_EXT[ext];
  }
  return null;
}
